import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { LayoutTemplate, Plus, Trash2 } from 'lucide-react';
import { api } from '../lib/api.ts';
import { extractError } from '../lib/types.ts';
import { PageHeader } from '../components/ui/PageHeader.tsx';
import { Button } from '../components/ui/Button.tsx';
import { Badge } from '../components/ui/Badge.tsx';
import { Modal, ModalFooter } from '../components/ui/Modal.tsx';
import { Input } from '../components/ui/Input.tsx';
import { EmptyState } from '../components/ui/EmptyState.tsx';
import { Skeleton } from '../components/ui/Skeleton.tsx';
import { ConfirmDialog } from '../components/ui/ConfirmDialog.tsx';
import { useToast } from '../components/ui/Toast.tsx';

interface CollectionTemplate {
  id: string;
  name: string;
  scope: 'listing' | 'detail';
  createdAt: string;
  updatedAt: string;
}

export function CmsCollectionTemplatesPage() {
  const { spaceId } = useParams<{ spaceId: string }>();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const toast = useToast();

  const [showCreate, setShowCreate] = useState(false);
  const [createName, setCreateName] = useState('');
  const [createScope, setCreateScope] = useState<'listing' | 'detail'>('listing');
  const [deleteTarget, setDeleteTarget] = useState<CollectionTemplate | null>(null);

  const { data: templates = [], isLoading } = useQuery<CollectionTemplate[]>({
    queryKey: ['collection-templates', spaceId],
    queryFn: () =>
      api
        .get(`/api/v1/cms/spaces/${spaceId}/collection-templates`)
        .then((r) => r.data),
    enabled: !!spaceId,
  });

  const createMut = useMutation({
    mutationFn: (body: { name: string; scope: string }) =>
      api
        .post(`/api/v1/cms/spaces/${spaceId}/collection-templates`, body)
        .then((r) => r.data as CollectionTemplate),
    onSuccess: (tmpl) => {
      qc.invalidateQueries({ queryKey: ['collection-templates', spaceId] });
      setShowCreate(false);
      setCreateName('');
      navigate(`/spaces/${spaceId}/collection-templates/${tmpl.id}`);
    },
    onError: (err) => toast.error(extractError(err)),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) =>
      api.delete(`/api/v1/cms/spaces/${spaceId}/collection-templates/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['collection-templates', spaceId] });
      setDeleteTarget(null);
      toast.success('Template eliminato');
    },
    onError: (err) => toast.error(extractError(err)),
  });

  return (
    <div>
      <PageHeader
        title="Collection Templates"
        description="Template riutilizzabili per le listing e detail page delle collections."
        action={
          <Button onClick={() => setShowCreate(true)} size="sm">
            <Plus className="w-3.5 h-3.5 mr-1.5" /> Nuovo template
          </Button>
        }
      />

      {isLoading ? (
        <div className="border border-zinc-200 rounded-xl overflow-hidden divide-y divide-zinc-100">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="flex items-center gap-3 p-4">
              <Skeleton className="w-4 h-4 rounded" />
              <Skeleton className="h-4 w-40 rounded" />
              <Skeleton className="h-5 w-14 rounded-full" />
            </div>
          ))}
        </div>
      ) : templates.length === 0 ? (
        <EmptyState
          icon={<LayoutTemplate size={26} />}
          title="Nessun template"
          description="Crea un template per personalizzare il layout di listing e detail page."
          action={{ label: 'Nuovo template', onClick: () => setShowCreate(true) }}
        />
      ) : (
        <div className="divide-y divide-zinc-100 border border-zinc-200 rounded-xl overflow-hidden">
          {templates.map((tmpl) => (
            <div
              key={tmpl.id}
              className="flex items-center justify-between p-4 hover:bg-zinc-50 cursor-pointer transition-colors"
              onClick={() =>
                navigate(`/spaces/${spaceId}/collection-templates/${tmpl.id}`)
              }
            >
              <div className="flex items-center gap-3">
                <LayoutTemplate className="w-4 h-4 text-zinc-400" />
                <p className="font-medium text-zinc-900 text-sm">{tmpl.name}</p>
                <Badge variant={tmpl.scope === 'listing' ? 'info' : 'purple'}>
                  {tmpl.scope}
                </Badge>
              </div>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setDeleteTarget(tmpl);
                }}
                className="p-1.5 rounded-lg text-zinc-400 hover:text-red-500 hover:bg-red-50 transition-colors"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      <Modal
        open={showCreate}
        onClose={() => {
          setShowCreate(false);
          setCreateName('');
        }}
        title="Nuovo template"
      >
        <div className="space-y-4">
          <Input
            label="Nome"
            value={createName}
            onChange={(e) => setCreateName(e.target.value)}
            placeholder="Blog Standard"
          />
          <div>
            <label className="block text-sm font-medium text-zinc-700 mb-1">Scope</label>
            <select
              value={createScope}
              onChange={(e) => setCreateScope(e.target.value as 'listing' | 'detail')}
              className="w-full border border-zinc-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900"
            >
              <option value="listing">Listing page</option>
              <option value="detail">Detail page</option>
            </select>
          </div>
        </div>
        <ModalFooter>
          <Button variant="ghost" size="sm" onClick={() => setShowCreate(false)}>
            Annulla
          </Button>
          <Button
            loading={createMut.isPending}
            disabled={!createName.trim() || createMut.isPending}
            onClick={() => createMut.mutate({ name: createName.trim(), scope: createScope })}
          >
            Crea
          </Button>
        </ModalFooter>
      </Modal>

      <ConfirmDialog
        open={!!deleteTarget}
        title="Elimina template"
        message={`Eliminare "${deleteTarget?.name}"? Le collections che lo usano perderanno il collegamento.`}
        confirmLabel="Elimina"
        variant="danger"
        loading={deleteMut.isPending}
        onConfirm={() => deleteTarget && deleteMut.mutate(deleteTarget.id)}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
